import { useMemo } from "react"
import type { CellMeta } from "../api/book"
import { formatEuro } from "../lib/format"

// Right-hand panel listing what's actually filled in the diary (cell values),
// grouped by voce label, with a running total.
export function DiaryValuesPanel({ cells, values, onSelect }: {
  cells: CellMeta[]
  values: Record<string, string>
  onSelect?: (code: string) => void
}) {
  const { rows, total } = useMemo(() => {
    const num = (v: string) => parseFloat((v || "").replace(",", ".")) || 0
    const rows = cells
      .filter((c) => (values[c.code] ?? "").trim() !== "")
      .map((c) => ({
        code: c.code,
        label: (c.label && c.label.trim()) || `Voce ${c.item}`,
        day: c.day,
        raw: values[c.code],
        n: num(values[c.code]),
      }))
      .sort((a, b) => a.label.localeCompare(b.label) || a.day - b.day)
    const total = rows.reduce((s, r) => s + r.n, 0)
    return { rows, total }
  }, [cells, values])

  return (
    <aside style={{ width: 320, flexShrink: 0, height: "100%", overflowY: "auto", borderLeft: "1px solid var(--border)", paddingLeft: 12 }}>
      <h3 style={{ margin: "0 0 4px 0", fontSize: 12, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 1 }}>
        Voci del diario
      </h3>
      <p style={{ margin: "0 0 14px 0", fontSize: 13, color: "var(--muted)" }}>
        Totale: <strong style={{ color: "var(--text)" }}>{formatEuro(total)}</strong>
        <span style={{ marginLeft: 6 }}>· {rows.length} compilate</span>
      </p>
      {rows.length === 0 && (
        <p style={{ fontSize: 12, color: "var(--muted)" }}>Nessuna voce compilata. Scansiona uno scontrino o compila le caselle.</p>
      )}
      {rows.map((r) => (
        <button
          key={r.code}
          type="button"
          onClick={() => onSelect?.(r.code)}
          title={onSelect ? "Vai alla casella" : undefined}
          style={{
            width: "100%",
            textAlign: "left",
            padding: "6px 4px",
            border: "none",
            borderBottom: "1px dashed var(--border)",
            background: "transparent",
            fontSize: 12,
            fontFamily: "inherit",
            display: "flex",
            gap: 8,
            alignItems: "baseline",
            cursor: onSelect ? "pointer" : "default",
            borderRadius: 4,
          }}
          onMouseEnter={(e) => { if (onSelect) e.currentTarget.style.background = "var(--surface)" }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
        >
          <span style={{ flex: 1, color: "var(--text)" }}>
            {r.label}
            <span style={{ color: "var(--muted)" }}> · G{r.day}</span>
          </span>
          <span style={{ color: "var(--accent)", whiteSpace: "nowrap" }}>{r.n ? formatEuro(r.n) : r.raw}</span>
        </button>
      ))}
    </aside>
  )
}
